import React, { useState } from "react";
import { Row, Col, Card, Button, Form, Alert } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Breadcrumb from "app/components/Breadcrumb";
import axios from "app/services/api";

const API_URL = "/api";

// Column order for the Product import file (headers must stay in this order)
const COLUMNS = [
  "Product Name",
  "Category",
  "Unit",
  "Unit Price",
  "Supplier",
  "Product Description",
  "Status"
];

// Units the file accepts — the label or the short form both work
const UNITS = "Number, Liter, Kilogram, Gram, Meter, Piece, Box, Dozen";

// Example rows for the sample file
const EXAMPLE_ROWS = [
  ["laptop", "Electronics", "Number", "40000", "Jindal Stainless Ltd", "14 inch office laptop", "active"],
  ["Iphone", "Electronics", "Number", "50000", "Jindal Stainless Ltd", "Company issued mobile handset", "active"],
  ["Pendrive", "Electronics", "Number", "1000", "Jindal Stainless Ltd", "32 GB USB storage drive", "active"],
  ["Burger paint", "Paint", "Liter", "200", "Jindal Stainless Ltd", "Enamel finish paint", "active"]
];

// Small quote-aware CSV parser (handles commas & quotes inside double-quoted fields)
const parseCSV = (text) => {
  const rows = [];
  let field = "";
  let record = [];
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const c = text[i];

    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += c;
      }
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ",") {
      record.push(field);
      field = "";
    } else if (c === "\n" || c === "\r") {
      // finish the record on newline (handle \r\n by skipping the paired char)
      if (c === "\r" && text[i + 1] === "\n") i++;
      record.push(field);
      rows.push(record);
      record = [];
      field = "";
    } else {
      field += c;
    }
  }
  // flush trailing field/record
  if (field !== "" || record.length > 0) {
    record.push(field);
    rows.push(record);
  }
  return rows;
};

export default function ImportProduct() {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  // Build & download a sample CSV template
  const handleDownloadSample = () => {
    const body = EXAMPLE_ROWS.map((row) => row.map((v) => `"${v}"`).join(",")).join("\n");
    const csv = COLUMNS.join(",") + "\n" + body + "\n";
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "products_sample.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleFileChange = (e) => {
    setResult(null);
    setFile(e.target.files[0] || null);
  };

  const handleSave = () => {
    if (!file) {
      alert("Please select a CSV file first.");
      return;
    }

    const reader = new FileReader();
    reader.onload = async (ev) => {
      const text = ev.target.result;
      const parsed = parseCSV(text).filter((r) => r.some((c) => String(c).trim() !== ""));

      if (parsed.length < 2) {
        alert("The file has no data rows.");
        return;
      }

      const headers = parsed[0].map((h) => String(h).trim());
      const rows = parsed.slice(1).map((cells) => {
        const obj = {};
        headers.forEach((h, idx) => {
          obj[h] = cells[idx] != null ? String(cells[idx]).trim() : "";
        });
        return obj;
      });

      try {
        setSaving(true);
        setResult(null);
        const { data } = await axios.post(`${API_URL}/products/import`, { rows });
        setResult(data);
      } catch (err) {
        console.error("Error importing products:", err);
        alert("Error importing products: " + (err.response?.data?.message || err.message));
      } finally {
        setSaving(false);
      }
    };
    reader.readAsText(file);
  };

  return (
    <div>
      <Breadcrumb
        routeSegments={[
          { name: "Product", path: "#" },
          { name: "Import Product" }
        ]}
      />

      <Card
        body
        style={{
          borderRadius: "10px",
          padding: "20px",
          marginTop: "20px",
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)"
        }}
      >
        <h5 style={{ marginBottom: "15px" }}>Import CSV file only</h5>

        {/* Download Sample Button */}
        <Row className="mb-3">
          <Col md="auto">
            <Button
              onClick={handleDownloadSample}
              style={{
                backgroundColor: "#6f42c1",
                borderColor: "#6f42c1",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                borderRadius: "5px",
                padding: "8px 16px"
              }}
            >
              <i className="i-Download"></i>
              Download Sample File
            </Button>
          </Col>
        </Row>

        {/* Instructions */}
        <p style={{ fontSize: "14px", color: "#959292ff", marginBottom: "10px" }}>
          The first line in downloaded sample file should remain as it is. Please do not change the order of columns in file.
        </p>
        <p style={{ fontSize: "14px", color: "#959292ff", marginBottom: "10px" }}>
          The correct column order is ({COLUMNS.join(", ")}).
        </p>
        <ul style={{ fontSize: "14px", color: "#0c0c0cff", paddingLeft: "20px", marginBottom: "20px" }}>
          <li>Category must match an existing product category.</li>
          <li>Supplier must match an existing supplier.</li>
          <li>Unit must be one of ({UNITS}).</li>
          <li>Unit Price must be a number greater than 0.</li>
          <li>Status is optional — it must be "active" or "inactive", and defaults to active when left blank.</li>
          <li>Product Description is optional.</li>
          <li>The Product Name + Category combination must be unique — existing combinations will be skipped.</li>
          <li>You must follow the file, otherwise you will get an error while importing the file.</li>
        </ul>

        {/* File Upload */}
        <Form.Group controlId="formFile" className="mb-3">
          <Form.Label>Upload File</Form.Label>
          <Form.Control
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            style={{
              borderRadius: "5px",
              padding: "6px 12px"
            }}
          />
          <small style={{ fontSize: "12px", color: "#777" }}>
            Please select a csv file (allowed file size 2MB)
          </small>
        </Form.Group>

        {/* Save Button */}
        <Row>
          <Col md="auto">
            <Button
              onClick={handleSave}
              disabled={saving}
              style={{
                backgroundColor: "#6f42c1",
                borderColor: "#6f42c1",
                color: "#fff",
                borderRadius: "5px",
                padding: "8px 16px"
              }}
            >
              <i className="i-Data-Save" style={{ marginRight: "8px" }}></i>
              {saving ? "Importing..." : "Save"}
            </Button>
          </Col>
        </Row>

        {/* Result Summary */}
        {result && (
          <Alert variant={result.errors?.length ? "warning" : "success"} className="mt-4">
            <strong>{result.message}</strong>

            {result.skipped?.length > 0 && (
              <>
                <hr />
                <div style={{ fontSize: "13px" }}>
                  <strong>Skipped (duplicates):</strong>
                  <ul className="mb-0">
                    {result.skipped.map((s, idx) => (
                      <li key={`sk-${idx}`}>
                        Row {s.row}{s.product_name ? ` (${s.product_name})` : ""} — {s.reason}
                      </li>
                    ))}
                  </ul>
                </div>
              </>
            )}

            {result.errors?.length > 0 && (
              <>
                <hr />
                <div style={{ fontSize: "13px" }}>
                  <strong>Errors:</strong>
                  <ul className="mb-0">
                    {result.errors.map((e, idx) => (
                      <li key={`er-${idx}`}>
                        Row {e.row}{e.product_name ? ` (${e.product_name})` : ""} — {e.reason}
                      </li>
                    ))}
                  </ul>
                </div>
              </>
            )}

            {result.inserted > 0 && (
              <div className="mt-3">
                <Button
                  size="sm"
                  style={{ backgroundColor: "#663399", border: "none" }}
                  onClick={() => navigate("/product/products")}
                >
                  View Product Master
                </Button>
              </div>
            )}
          </Alert>
        )}
      </Card>
    </div>
  );
}
